/** Page-load state of the What's new flow, kept outside React so a
 *  remounted shell (a layout change, an error boundary) doesn't offer
 *  the same version twice, and so the dialog can be opened from an
 *  effect without a state update inside it.
 *
 *  * ``handled``: versions acknowledged or whose dialog was opened in
 *    this page load. The next load asks the server again.
 *  * the open dialog: the notes it shows and the version its close
 *    acknowledges (captured when it opened, so a deploy while it is
 *    open doesn't change what is acknowledged). */
import * as React from "react";

import type { ReleaseNote } from "@/api/types";
import { closeWhatsNewChannel } from "@/features/whatsNew/channel";

const handled = new Set<string>();

export function isHandled(version: string): boolean {
  return handled.has(version);
}

export function markHandled(version: string): void {
  handled.add(version);
}

export type OpenDialog = {
  open: boolean;
  /** The version closing acknowledges. */
  version: string;
  /** The user's previous position, for "See all changes". */
  since: string | null;
  notes: ReleaseNote[];
  omitted: number;
};

let dialog: OpenDialog | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function showDialog(next: Omit<OpenDialog, "open">): void {
  dialog = { ...next, open: true };
  emit();
}

/** The version the open dialog acknowledges, or null when none is open. */
export function openDialogVersion(): string | null {
  return dialog?.open ? dialog.version : null;
}

/** Close, keeping the content for the exit transition. */
export function hideDialog(): void {
  if (!dialog?.open) return;
  dialog = { ...dialog, open: false };
  emit();
}

export function useWhatsNewDialog(): OpenDialog | null {
  return React.useSyncExternalStore(
    subscribe,
    () => dialog,
    () => null,
  );
}

/** Tests only: start over as if the page had just loaded. */
export function forgetWhatsNew(): void {
  handled.clear();
  dialog = null;
  closeWhatsNewChannel();
  emit();
}
